import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import ProjectPipelineRow from "./ProjectPipelineRow";
import ProjectStatusDetailBar from "./ProjectStatusDetailBar";
import TechnicalConsultantInfo from "../../../../common/components/TechnicalConsultantInfo/TechnicalConsultantInfo";
import { FILTER_TAB_ICONS, MobilePhoneTabIcon } from "./FilterTabIcons";
import { ProjectStatusData } from "../../types";
import { getPartnerProjectDetailsPath } from "../../utils/projectNavigation";
import { useTimelineStepPermissions } from "../../../../hooks/useTimelineStepPermissions";

interface ProjectStatusSectionProps {
  projects: ProjectStatusData[];
  filters: string[];
  partnerId: number;
  viewAllEnabled?: boolean;
}

const PROJECT_STATUS_SCROLL_THRESHOLD = 5;

const ProjectStatusSection: React.FC<ProjectStatusSectionProps> = ({
  projects,
  filters,
  partnerId,
  viewAllEnabled = true,
}) => {
  const navigate = useNavigate();
  const { canAccessTimeline } = useTimelineStepPermissions();
  const canOpenProjectDetails = viewAllEnabled && canAccessTimeline;
  const [activeFilter, setActiveFilter] = useState(filters[0] ?? "All");
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    if (!filters.includes(activeFilter)) {
      setActiveFilter(filters[0] ?? "All");
    }
  }, [activeFilter, filters]);

  const filteredProjects = useMemo(() => {
    if (activeFilter === "All") return projects;
    return projects.filter((p) => p.technologyFilter === activeFilter);
  }, [activeFilter, projects]);

  const handleViewAll = () => {
    if (!viewAllEnabled) return;

    if (!partnerId || Number.isNaN(partnerId)) {
      return;
    }

    navigate(`/partner/${partnerId}/dashboard/projects`);
  };

  const handleProjectClick = (project: ProjectStatusData) => {
    if (!canOpenProjectDetails) return;
    if (!partnerId || Number.isNaN(partnerId) || !project.name) return;

    navigate(getPartnerProjectDetailsPath(partnerId, project.name), {
      state: project.technology ? { selectedTechnology: project.technology } : undefined,
    });
  };

  const toggleProjectExpanded = (projectId: string) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  return (
    <div className="project-status-section">
      <div className="project-status-section__intro">
        <div className="project-status-section__header">
          <div className="project-status-section__header-text">
            <h3 className="project-status-section__title">Project Status</h3>
            <p className="project-status-section__subtitle">
              Integration &amp; validation by technology
            </p>
          </div>
          {viewAllEnabled ? (
            <button
              type="button"
              className="project-status-section__view-all"
              onClick={handleViewAll}
            >
              View All
              <Iconography icon="chevronRight" size={IconographySizes.Small} />
            </button>
          ) : null}
        </div>

        <div className="project-status-section__tabs">
          {filters.map((filter) => {
            const TabIcon = FILTER_TAB_ICONS[filter] ?? MobilePhoneTabIcon;

            return (
              <button
                key={filter}
                type="button"
                className={`project-status-section__tab ${
                  activeFilter === filter ? "project-status-section__tab--active" : ""
                }`}
                onClick={() => setActiveFilter(filter)}
              >
                <TabIcon className="project-status-section__tab-icon" />
                <span className="project-status-section__tab-label">{filter}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div
        className={`project-status-section__table${
          filteredProjects.length > PROJECT_STATUS_SCROLL_THRESHOLD
            ? " project-status-section__table--has-scroll"
            : ""
        }`}
      >
        <div className="project-status-section__table-header">
          <span className="project-status-section__table-header-project">Project</span>
          <span className="project-status-section__table-header-pipeline">Pipeline</span>
        </div>

        <div
          className={`project-status-section__table-body${
            filteredProjects.length > PROJECT_STATUS_SCROLL_THRESHOLD
              ? " project-status-section__table-body--scrollable"
              : ""
          }`}
        >
          {filteredProjects.map((project) => {
            const isExpanded = expandedProjectIds.has(project.id);

            return (
              <div
                key={project.id}
                className={`project-status-section__row${
                  isExpanded ? " project-status-section__row--expanded" : ""
                }`}
              >
                <div className="project-status-section__project">
                  <button
                    type="button"
                    className={`project-status-section__expand${
                      isExpanded ? " project-status-section__expand--open" : ""
                    }`}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${project.name}`}
                    aria-expanded={isExpanded}
                    onClick={() => toggleProjectExpanded(project.id)}
                  >
                    <Iconography icon="chevronDown" size={IconographySizes.Small} />
                  </button>
                  <div className="project-status-section__project-main">
                    <span
                      className={`project-status-section__badge project-status-section__badge--${project.platformBadgeVariant}`}
                    >
                      {project.platformBadge}
                    </span>
                    {canOpenProjectDetails ? (
                      <button
                        type="button"
                        className="project-status-section__name project-status-section__name--link"
                        onClick={() => handleProjectClick(project)}
                      >
                        {project.name}
                      </button>
                    ) : (
                      <span className="project-status-section__name">{project.name}</span>
                    )}
                    <span className="project-status-section__sub">{project.subLabel}</span>
                    {isExpanded && project.technicalConsultant && (
                      <TechnicalConsultantInfo
                        name={project.technicalConsultant}
                        className="project-status-section__consultant"
                      />
                    )}
                  </div>
                </div>

                <div className="project-status-section__pipeline-area">
                  <div className="project-status-section__pipeline-top">
                    <ProjectPipelineRow stages={project.stages} />
                    <span className="project-status-section__percent">
                      {project.completionPercent}%
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="project-status-section__row-detail">
                      <ProjectStatusDetailBar details={project.expandedDetails} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="project-status-section__footer">
          {filteredProjects.length} projects shown
        </div>
      </div>
    </div>
  );
};

export default ProjectStatusSection;
